import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { PaymentService } from '../payment.service';
import { Settlement } from '../payment.type';
import { Viewport } from '../../shared/viewport';
import { SupplierInfoDialog } from '../../supplier/supplier-info-dialog/supplier-info-dialog';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { CopyRialButton } from '../../shared/components/copy-rial-button/copy-rial-button';
import { PaymentState } from '../payment.state';
import { statusClass } from '../payment-format';
import { SettlementProofDialog, SettlementProofDialogResult } from '../settlement-proof-dialog/settlement-proof-dialog';
import { ManualSettlementDialog } from '../manual-settlement-dialog/manual-settlement-dialog';
import { ReportExportDialog } from '../report-export-dialog/report-export-dialog';
import { SettlementProofsDialog } from '../settlement-proofs-dialog/settlement-proofs-dialog';
import { SettlementItemsDialog } from '../settlement-items-dialog/settlement-items-dialog';
import { SettlementPaymentDialog } from '../settlement-payment-dialog/settlement-payment-dialog';
import { AuthService } from '../../auth/auth.service';

/** The Payments "Settlements" tab: what was recorded as paid, and what still awaits its transfer. */
@Component({
  selector: 'app-payment-settlements',
  imports: [
    CommonModule, FormsModule, MatButtonToggleModule, MatTableModule, MatButtonModule, MatIconModule, MatFormFieldModule,
    MatSelectModule, MatProgressSpinnerModule, MatTooltipModule, MatPaginatorModule, CopyRialButton,
  ],
  templateUrl: './payment-settlements.html',
  styleUrl: './payment-settlements.scss',
})
export class PaymentSettlements implements OnInit {
  readonly state = inject(PaymentState);
  readonly canEdit = inject(AuthService).canWrite('suppliers_payments');
  private paymentService = inject(PaymentService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  readonly isMobile$ = inject(Viewport).isMobile$;

  readonly settlementClass = statusClass;
  readonly pageSizeOptions = [25, 50, 100, 200];
  readonly columns = ['created', 'supplier', 'amount', 'status', 'paidFrom', 'note', 'covers', 'proof', 'actions'];

  settlements: Settlement[] = [];
  total = 0;
  pageIndex = 0;
  pageSize = 50;
  loading = false;
  error: string | null = null;

  /** '' = all. Pending is what the admin still has to pay. */
  status = '';
  /**
   * Which day the From/To filter bounds: the day recorded, or the day paid —
   * what the Hesabfa export and comparison count, so the counts match there.
   * Kept in the URL so a reload or a shared link keeps it.
   */
  dateBy: 'created' | 'paid' = this.route.snapshot.queryParamMap.get('dateBy') === 'paid' ? 'paid' : 'created';

  ngOnInit(): void {
    this.load();
    this.state.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => { this.pageIndex = 0; this.load(); });
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getSettlements({
      ...this.state.filters(),
      status: this.status || undefined,
      dateBy: this.dateBy === 'paid' ? 'paid' : undefined,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    }).subscribe({
      next: res => {
        this.settlements = res.settlements;
        this.total = res.total;
        this.loading = false;
      },
      error: err => { this.error = err?.error?.error ?? 'Failed to load settlements'; this.loading = false; },
    });
  }

  onFilterChange(): void {
    this.pageIndex = 0;
    this.load();
  }

  onDateByChange(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { dateBy: this.dateBy === 'paid' ? 'paid' : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.onFilterChange();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.load();
  }

  /** A bonus or the fix for a mistake, covering no runs or contracts. */
  recordManual(): void {
    this.dialog.open(ManualSettlementDialog, { width: '520px', maxWidth: '95vw' })
      .afterClosed().subscribe(created => { if (created) this.load(); });
  }

  /** The accountant's workbook for one day, narrowed by this tab's supplier and status filters. */
  exportReport(): void {
    this.dialog.open(ReportExportDialog, {
      width: '460px',
      maxWidth: '95vw',
      data: {
        supplierId: this.state.supplierId || undefined,
        status: this.status || undefined,
        // Paid-on in the list means the report should count days the same way.
        dateBy: this.dateBy === 'paid' ? 'paid' : '',
      },
    });
  }

  openSupplierInfoDialog(supplierId: string, supplierName?: string): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(SupplierInfoDialog, {
      data: { supplierId: supplierId, supplierUsername: supplierName },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  /**
   * Opens the same proof-capture dialog used at settlement creation — the
   * payment proof is often only in hand once the transfer is
   * actually sent, which is exactly when this is called. Proofs attached here
   * are added to any already attached.
   */
  markPaid(s: Settlement): void {
    const ref = this.dialog.open(SettlementProofDialog, {
      width: '440px',
      maxWidth: '95vw',
      data: {
        title: 'Mark settlement paid',
        submitLabel: 'Mark paid',
        summary: s.supplier_name,
        amount: s.amount,
        existingProofs: s.proof_count,
        askPayment: true,
      },
    });
    ref.afterClosed().subscribe((result: SettlementProofDialogResult | null) => {
      if (!result?.payment) return;
      this.paymentService.markSettlementPaid(s.id, result.payment, result.note || undefined, result.proofFiles).subscribe({
        next: () => {
          this.snackBar.open('Marked paid', 'Close', { duration: 3000 });
          this.load();
        },
        error: err => this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 5000 }),
      });
    });
  }

  /** Records or corrects which account a paid settlement left from, its fee and tracking number. */
  editPayment(s: Settlement): void {
    this.dialog.open(SettlementPaymentDialog, { data: { settlement: s }, width: '460px', maxWidth: '95vw' })
      .afterClosed().subscribe(changed => { if (changed) this.load(); });
  }

  coversItems(s: Settlement): boolean {
    return (s.run_count ?? 0) + (s.contract_count ?? 0) > 0;
  }

  itemsLabel(s: Settlement): string {
    const runs = s.run_count ?? 0;
    const contracts = s.contract_count ?? 0;
    return [
      runs ? `${runs} run${runs === 1 ? '' : 's'}` : '',
      contracts ? `${contracts} contract${contracts === 1 ? '' : 's'}` : '',
    ].filter(Boolean).join(' + ');
  }

  /** The transfer runs and manual contracts this settlement pays for. */
  viewItems(s: Settlement): void {
    this.dialog.open(SettlementItemsDialog, { data: { settlement: s }, width: '760px', maxWidth: '95vw', maxHeight: '90vh' });
  }

  /** View, add or remove this settlement's payment proofs — paid or not. */
  editProofs(s: Settlement): void {
    this.dialog.open(SettlementProofsDialog, { data: { settlement: s, readOnly: !this.canEdit }, width: '640px', maxWidth: '95vw', maxHeight: '90vh' })
      .afterClosed().subscribe(changed => { if (changed) this.load(); });
  }

  /**
   * Removes a settlement that is not paid yet — recorded by mistake, or for
   * the wrong items. What it covered goes back to unpaid; a paid one is kept.
   */
  remove(s: Settlement): void {
    if (s.status === 'Paid') return;
    const covers = this.coversItems(s)
      ? ` The ${this.itemsLabel(s)} it covers will go back to unpaid.`
      : '';
    this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Remove settlement',
        message: `Remove the unpaid ${s.amount.toLocaleString()} Toman settlement for ${s.supplier_name}?${covers} Its proofs are deleted too. This cannot be undone.`,
      },
    }).afterClosed().subscribe(ok => {
      if (!ok) return;
      this.paymentService.deleteSettlement(s.id).subscribe({
        next: () => {
          this.snackBar.open('Settlement removed', 'Close', { duration: 4000 });
          this.state.reload();
        },
        error: err => {
          this.snackBar.open(err?.error?.error ?? 'Failed to remove the settlement', 'Close', { duration: 6000 });
          this.load();
        },
      });
    });
  }
}
